// The dashboard's Graphics tab (views/companies/dashboard-tab-graphics.js): the reply of dashboard/alertStats as tiles,
// a heat map of missed alerts, the best and worst guards and alerts answered week by week.

export const EMPTY_MESSAGE = 'No alerts in this period yet.';
export const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const SHORT_DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Guards under this answered % are shown in the warning colour
export const WARNING_BELOW = 90;
// With this many ranked guards or fewer, one list instead of best and worst
export const ONE_LIST_UP_TO = 10;
export const LIST_SIZE = 5;

const DAY_MS = 86400000;
export const PERIODS = [
	{ value: 'day', label: 'Last day', days: 1, previous: 'the previous day' },
	{ value: 'week', label: 'Last 7 days', days: 7, previous: 'the previous 7 days' },
	{ value: 'month', label: 'Last 30 days', days: 30, previous: 'the previous 30 days' },
	{ value: '6month', label: 'Last 6 months', days: 182, previous: 'the previous 6 months' },
];
export const DEFAULT_PERIOD = 'week';

// The period asked for: one of PERIODS back from now, or a chosen range ({from, to} in milliseconds)
export function periodRange(period, nowMs, custom) {
	if (period === 'custom' && custom && custom.from < custom.to) {
		return { from: custom.from, to: Math.min(custom.to, nowMs) };
	}
	const found = PERIODS.find((p) => p.value === period) || PERIODS.find((p) => p.value === DEFAULT_PERIOD);
	return { from: nowMs - found.days * DAY_MS, to: nowMs };
}

// "the previous 7 days": the period before, of the same length
export function previousLabel(period, days) {
	const found = PERIODS.find((p) => p.value === period);
	if (found) {
		return found.previous;
	}
	return days === 1 ? 'the previous day' : 'the previous ' + days + ' days';
}

export function formatPct(value) {
	if (value === null || value === undefined || isNaN(Number(value))) {
		return '–';
	}
	const rounded = Math.round(Number(value) * 10) / 10;
	return (Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)) + '%';
}

// The change against the previous period: "up 3 points on the previous 7 days" (answered %: the difference in
// percentage points), "up 4 on the previous 7 days" (a count), "the same as the previous 7 days". Null when the previous period
// had no alerts to compare with.
export function changeText(current, previous, kind, previousName) {
	if (previous === null || previous === undefined || current === null || current === undefined) {
		return null;
	}
	const diff = kind === 'pct' ? Math.round((Number(current) - Number(previous)) * 10) / 10 : Number(current) - Number(previous);
	if (diff === 0) {
		return 'The same as ' + previousName;
	}
	const amount = kind === 'pct' ? formatPct(Math.abs(diff)).replace('%', Math.abs(diff) === 1 ? ' point' : ' points') : String(Math.abs(diff));
	return (diff > 0 ? 'Up ' : 'Down ') + amount + ' on ' + previousName;
}

// The four tiles: [{key, label, value, sub, change}]
export function tiles(stats, period) {
	const t = stats.tiles;
	const prev = t.previous || {};
	const previousName = previousLabel(period, stats.period ? stats.period.days : 7);
	const hadBefore = prev.total > 0;
	return [
		{
			key: 'answered', label: 'Alerts answered', value: formatPct(t.answered_pct),
			sub: t.total > 0 ? t.answered + ' of ' + t.total + ' alerts' : EMPTY_MESSAGE,
			change: t.total > 0 && hadBefore ? changeText(t.answered_pct, prev.answered_pct, 'pct', previousName) : null,
		},
		{
			key: 'missed', label: 'Missed alerts', value: String(t.missed),
			sub: 'of ' + t.total + ' alerts',
			change: hadBefore ? changeText(t.missed, prev.missed, 'count', previousName) : null,
		},
		{
			key: 'away', label: 'Answered away from the duty location', value: String(t.away),
			sub: 'of ' + t.answered + ' alerts answered',
			change: hadBefore ? changeText(t.away, prev.away, 'count', previousName) : null,
		},
		{
			key: 'onduty', label: 'Guards on duty now', value: String(t.on_duty_now),
			sub: t.on_duty_now === 1 ? 'guard with a duty running now' : 'guards with a duty running now',
			change: null,
		},
	];
}

export function hourLabel(hour) {
	return (hour < 10 ? '0' : '') + hour + ':00';
}

export function heatTooltip(day, hour, count) {
	return DAY_NAMES[day] + ' ' + hourLabel(hour) + '–' + hourLabel((hour + 1) % 24) + ': ' + count + (count === 1 ? ' missed alert' : ' missed alerts');
}

// The heat map's grid: a row per day of the week, a column per hour that had duties (and any hour with a missed alert)
export function heatGrid(heatmap) {
	const cells = (heatmap && heatmap.cells) || [];
	const hours = new Set((heatmap && heatmap.hours) || []);
	cells.forEach((cell) => hours.add(cell.hour));
	const columns = Array.from(hours).sort((a, b) => a - b);
	const counts = {};
	let max = 0;
	cells.forEach((cell) => {
		counts[cell.day + ':' + cell.hour] = cell.missed;
		max = Math.max(max, cell.missed);
	});
	const rows = DAY_NAMES.map((name, day) => ({
		day, name, short: SHORT_DAY_NAMES[day],
		cells: columns.map((hour) => ({ hour, missed: counts[day + ':' + hour] || 0 })),
	}));
	return { columns, rows, max };
}

// A red scale by the count: pale pink (few missed) to dark red (the most); 0 is a very light grey
const NONE = [241, 243, 245];
const PINK = [253, 221, 224];
// Through a clear red, so the middle of the scale is red rather than a muddy brown
const RED = [226, 54, 54];
const RED_AT = 0.55;
const DARK_RED = [127, 0, 0];
function heatRgb(count, max) {
	if (!count || !max) {
		return NONE;
	}
	const share = Math.min(1, count / max);
	const [from, to, t] = share <= RED_AT ? [PINK, RED, share / RED_AT] : [RED, DARK_RED, (share - RED_AT) / (1 - RED_AT)];
	return from.map((value, i) => Math.round(value + (to[i] - value) * t));
}

export function heatColour(count, max) {
	return 'rgb(' + heatRgb(count, max).join(', ') + ')';
}

// Relative luminance (WCAG)
function luminance(rgb) {
	const [r, g, b] = rgb.map((c) => {
		const s = c / 255;
		return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
	});
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Black text where it reads better than white, by contrast; white text on the darker reds. Choosing the better of
// black and white gives a contrast of at least 4.5 on any colour.
export const HEAT_TEXT_DARK = '#000';
export const HEAT_TEXT_LIGHT = '#fff';
export function heatTextDark(count, max) {
	const l = luminance(heatRgb(count, max));
	return (l + 0.05) / 0.05 >= 1.05 / (l + 0.05);
}

// The contrast of the text colour heatTextDark chooses on the square
export function heatTextContrast(count, max) {
	const l = luminance(heatRgb(count, max));
	return heatTextDark(count, max) ? (l + 0.05) / 0.05 : 1.05 / (l + 0.05);
}

// Best and worst guards (the reply lists the ranked guards best first): two lists of 5, or one with 10 or fewer
export function guardLists(guards) {
	const list = Array.isArray(guards) ? guards : [];
	if (list.length <= ONE_LIST_UP_TO) {
		return { single: list, best: [], worst: [] };
	}
	return { single: null, best: list.slice(0, LIST_SIZE), worst: list.slice(-LIST_SIZE).reverse() };
}

export function isWarning(pct) {
	return pct !== null && pct !== undefined && Number(pct) < WARNING_BELOW;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function shortDate(ymd) {
	const [, m, d] = String(ymd).split('-').map(Number);
	return d + ' ' + MONTHS[m - 1];
}

// "28 Oct – 3 Nov"
export function weekLabel(week) {
	return shortDate(week.from) + ' – ' + shortDate(week.to);
}

export function weekTooltip(week) {
	if (!week.total) {
		return weekLabel(week) + ': no alerts';
	}
	return weekLabel(week) + ': ' + formatPct(week.answered_pct) + ' answered (' + week.answered + ' of ' + week.total + ')';
}

// The week-by-week chart in a box of width x height: the y axis from a round number under the lowest week (and the
// target) up to 100%; points only for weeks with alerts
export function lineChart(weeks, target, width, height, pad) {
	const list = Array.isArray(weeks) ? weeks : [];
	const values = list.filter((w) => w.total > 0).map((w) => Number(w.answered_pct));
	const lowest = Math.min(target, ...values);
	const yMin = Math.max(0, Math.floor((lowest - 5) / 10) * 10);
	const innerW = width - pad.left - pad.right;
	const innerH = height - pad.top - pad.bottom;
	const x = (i) => pad.left + (list.length <= 1 ? innerW / 2 : (i * innerW) / (list.length - 1));
	const y = (pct) => pad.top + ((100 - pct) * innerH) / (100 - yMin);
	const points = list.map((w, i) => (w.total > 0 ? { x: x(i), y: y(Number(w.answered_pct)), week: w, index: i } : null)).filter(Boolean);
	const ticks = [];
	for (let t = yMin; t <= 100; t += yMin <= 50 ? 20 : 10) {
		ticks.push({ value: t, y: y(t) });
	}
	return {
		points,
		path: points.map((p, i) => (i === 0 ? 'M' : 'L') + p.x.toFixed(1) + ' ' + p.y.toFixed(1)).join(' '),
		targetY: y(target),
		ticks,
		x,
		yMin,
	};
}

// True when the dashboard's selector (options, or groups of options) has this value ("client:name:31")
export function optionListed(options, value) {
	return (Array.isArray(options) ? options : []).some((option) => option && (option.value === value || optionListed(option.options, value)));
}

export function hasWeeks(weeks) {
	return Array.isArray(weeks) && weeks.some((w) => w.total > 0);
}
